import { MoreHorizontal, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/students');

interface Student {
  id: string;
  studentNumber: string;
  nameFirst: string;
  nameLast: string;
  email: string;
  structureId: string;
  enrollmentYear: number;
  status: string;
}

interface OrgNode {
  id: string;
  nameFormal?: string;
  name?: string;
  children?: OrgNode[];
}

export default function StudentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);

  const [structures, setStructures] = useState<OrgNode[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [studentRes, orgRes] = await Promise.all([fetch(API_BASE), fetch(apiUrl('/api/organizations'))]);

        if (studentRes.ok) {
          setStudents((await studentRes.json()) as Student[]);
        }
        if (orgRes.ok) {
          setStructures((await orgRes.json()) as OrgNode[]);
        }
      } catch (error) {
        console.error('Failed to fetch data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStructureName = (id: string) => {
    // Flatten structures if they are tree, or assume flat list from API?
    // /api/organizations returns tree usually. We need to find recursively or simplified.
    // For now, assuming naive lookup or flat list if API supports it.
    // Actually /api/organizations returns list of organizations (check source).
    // Let's assume linear search or flattened map.
    // To be safe, let's just show ID if not found, but try to find it.

    // Helper to find in tree
    const findInTree = (nodes: OrgNode[], targetId: string): string | null => {
      for (const node of nodes) {
        if (node.id === targetId) return node.nameFormal || node.name || null;
        if (node.children) {
          const found = findInTree(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    return findInTree(structures, id) || `${id.substring(0, 8)}...`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ENROLLED':
        return <Badge className="bg-green-600">在学</Badge>;
      case 'LEAVE':
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
            休学
          </Badge>
        );
      case 'GRADUATED':
        return <Badge variant="outline">卒業</Badge>;
      case 'WITHDRAWN':
        return <Badge variant="destructive">退学</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">学籍マスタ管理</h1>
          <p className="text-gray-500 mt-1">在校生および合格者の学籍データを管理します。</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">CSVインポート</Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 shadow-md"
            onClick={() => navigate('/admin/enrollment/students/new')}
          >
            <Plus className="w-4 h-4 mr-2" /> 新規学籍登録
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input placeholder="学籍番号・氏名で検索..." className="pl-9" />
        </div>
      </div>

      {/* Table */}
      <Card className="border-t-4 border-t-indigo-500 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>学籍番号</TableHead>
                <TableHead>氏名</TableHead>
                <TableHead>所属 (学科/コース)</TableHead>
                <TableHead>入学年度</TableHead>
                <TableHead>ステータス</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    読み込み中...
                  </TableCell>
                </TableRow>
              )}
              {!loading && students.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    学生データがありません。
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                students.map((student) => (
                  <TableRow key={student.id} className="group hover:bg-gray-50">
                    <TableCell className="font-mono">{student.studentNumber}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {student.nameLast} {student.nameFirst}
                        </span>
                        <span className="text-xs text-gray-500">{student.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-normal">
                          {getStructureName(student.structureId)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>{student.enrollmentYear}</TableCell>
                    <TableCell>{getStatusBadge(student.status)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => navigate(`/admin/enrollment/students/${student.id}`)}>
                            詳細・編集
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">削除/除籍</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
